'use client'

/**
 * Self-contained inline-SVG app store badges.
 *
 * Google Play -> opens the real SarvamAstro Play Store listing.
 * App Store   -> opens a "Coming Soon" modal because the iOS app is
 *                not yet published. When the iOS app ships, replace
 *                the modal handler with an `<a href={APP_STORE_URL}>`
 *                wrapper like GooglePlayBadge.
 *
 * Badges are inline SVG (no image files needed). Apple & Google brand
 * guidelines suggest official badge artwork on production; these are
 * a reasonable visual stand-in until those PNGs are added.
 */

import { useEffect, useRef, useState } from 'react'

const PLAY_URL = 'https://play.google.com/store/apps/details?id=com.sarvamastro.app'

type BadgeProps = {
  /** Visual height of the badge in px. Width scales automatically. */
  height?: number
}

function GooglePlayBadge({ height = 48 }: BadgeProps) {
  const w = (height / 48) * 162
  return (
    <a
      href={PLAY_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Get it on Google Play"
      style={{ display: 'inline-flex', lineHeight: 0 }}
    >
      <svg
        width={w}
        height={height}
        viewBox="0 0 162 48"
        role="img"
        aria-hidden="true"
        focusable="false"
      >
        <rect
          x="0.5"
          y="0.5"
          width="161"
          height="47"
          rx="8"
          fill="#000"
          stroke="rgba(255,255,255,0.35)"
        />
        {/* Play triangle */}
        <g transform="translate(14 12)">
          <path d="M0 1.2v21.6c0 .9.97 1.46 1.74 1L20 13 1.74.2A1.16 1.16 0 0 0 0 1.2Z" fill="#00D2FF" />
          <path d="M14.3 8.7 1.74.2 0 1.2l12.1 12.1 2.2-4.6Z" fill="#00F076" />
          <path d="M14.3 17.3 0 22.8l1.74.99L14.3 17.3l-2.2-4Z" fill="#FF3A44" />
          <path d="m14.3 8.7 2.2 4.6-2.2 4 5.7-3.3c1.3-.74 1.3-2.85 0-3.6L14.3 8.7Z" fill="#FFC900" />
        </g>
        <text x="44" y="20" fill="#fff" fontFamily="Arial, sans-serif" fontSize="8" opacity="0.85">
          GET IT ON
        </text>
        <text x="44" y="36" fill="#fff" fontFamily="Arial, sans-serif" fontSize="16" fontWeight="600">
          Google Play
        </text>
      </svg>
    </a>
  )
}

/* App Store badge SVG, factored out so both the trigger button and any
   future real link share the same artwork. */
function AppStoreSvg({ height }: { height: number }) {
  const w = (height / 48) * 162
  return (
    <svg
      width={w}
      height={height}
      viewBox="0 0 162 48"
      role="img"
      aria-hidden="true"
      focusable="false"
    >
      <rect
        x="0.5"
        y="0.5"
        width="161"
        height="47"
        rx="8"
        fill="#000"
        stroke="rgba(255,255,255,0.35)"
      />
      {/* Apple logo */}
      <path
        transform="translate(16 11) scale(0.052)"
        fill="#fff"
        d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5c0 26.2 4.8 53.3 14.4 81.2 12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"
      />
      <text x="44" y="20" fill="#fff" fontFamily="Arial, sans-serif" fontSize="8" opacity="0.85">
        Download on the
      </text>
      <text x="44" y="36" fill="#fff" fontFamily="Arial, sans-serif" fontSize="16" fontWeight="600">
        App Store
      </text>
    </svg>
  )
}

/**
 * Accessible "Coming Soon" modal — opens when the App Store badge is
 * clicked. Closes on Escape, backdrop click, or the Close button.
 * Returns focus to the trigger after close.
 */
function ComingSoonModal({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const closeBtnRef = useRef<HTMLButtonElement | null>(null)

  // Focus management: send focus into the dialog when it opens, and
  // return it to the badge button when it closes. Also lock body
  // scroll while open so the page behind doesn't move.
  useEffect(() => {
    if (open) {
      const previouslyFocused = document.activeElement as HTMLElement | null
      closeBtnRef.current?.focus()
      const prevOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = prevOverflow
        previouslyFocused?.focus()
      }
    }
  }, [open])

  // Escape closes.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="coming-soon-title"
      aria-describedby="coming-soon-desc"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        animation: 'storeBadgeFade 160ms ease-out',
      }}
    >
      {/* @keyframes storeBadgeFade defined in app/globals.css.
          Avoids inline <style> in client components — see the
          testimonial carousel comment for the hydration rationale. */}
      {/* Stop click propagation so clicks inside the card don't close. */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--bg-card)',
          color: 'var(--text-primary)',
          border: '1px solid var(--border)',
          borderRadius: '16px',
          padding: '28px 28px 24px',
          maxWidth: '380px',
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 24px 60px rgba(0, 0, 0, 0.5)',
        }}
      >
        {/* Apple glyph */}
        <div
          aria-hidden="true"
          style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border)',
            margin: '0 auto 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg width="32" height="32" viewBox="0 0 384 512" aria-hidden="true">
            <path
              fill="currentColor"
              d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5c0 26.2 4.8 53.3 14.4 81.2 12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"
            />
          </svg>
        </div>

        <h2
          id="coming-soon-title"
          style={{
            fontFamily: 'Playfair Display, serif',
            fontSize: '22px',
            fontWeight: 800,
            margin: '0 0 8px',
            color: 'var(--text-primary)',
          }}
        >
          Coming Soon
        </h2>

        <p
          id="coming-soon-desc"
          style={{
            color: 'var(--text-muted)',
            fontSize: '14px',
            lineHeight: 1.6,
            margin: '0 0 20px',
          }}
        >
          The SarvamAstro app is currently unavailable on the App Store.
          It is live on Google Play — Apple version coming soon.
        </p>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <a
            href={PLAY_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClose}
            className="btn-primary"
            style={{ fontSize: '14px', padding: '10px 22px' }}
          >
            Get it on Google Play
          </a>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            className="btn-secondary"
            style={{ fontSize: '14px', padding: '10px 22px' }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

function AppStoreBadge({ height = 48 }: BadgeProps) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Download on the App Store (Coming Soon)"
        style={{
          all: 'unset',
          cursor: 'pointer',
          display: 'inline-flex',
          lineHeight: 0,
          borderRadius: '8px',
        }}
      >
        <AppStoreSvg height={height} />
      </button>
      <ComingSoonModal open={open} onClose={() => setOpen(false)} />
    </>
  )
}

/**
 * Both badges in a row or column. Drop-in replacement for the old
 * <Image> badge blocks used across the site.
 */
export default function StoreBadges({
  direction = 'row',
  height = 48,
  wrap = true,
}: {
  direction?: 'row' | 'column'
  height?: number
  /** Allow the badges to wrap onto a new line when space is tight.
      Set false in tight rows (e.g. the navbar) to keep them aligned. */
  wrap?: boolean
}) {
  return (
    <div style={{ display: 'flex', flexDirection: direction, gap: '12px', flexWrap: wrap ? 'wrap' : 'nowrap', alignItems: 'center' }}>
      <GooglePlayBadge height={height} />
      <AppStoreBadge height={height} />
    </div>
  )
}

export { GooglePlayBadge, AppStoreBadge }
